import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { API } from "../../lib/api";
import { ArrowLeft, ArrowRight, Sparkles, Plus, X, Wand2, Check, Play } from "lucide-react";

/**
 * Compose Wizard — 5 steps to save a persona-wrapped custom model.
 *
 * 1. Identity   — name, avatar, description, tags
 * 2. Base brain — pick the underlying LLM
 * 3. Mission    — system prompt + tone
 * 4. Few-shot   — up to 5 example exchanges
 * 5. Preview    — stream a test prompt, then save
 */
export default function StudioCompose() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [brains, setBrains] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testOutput, setTestOutput] = useState("");
  const [testing, setTesting] = useState(false);
  const [testPrompt, setTestPrompt] = useState("Introduce yourself in two sentences.");
  const [savedId, setSavedId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    avatar: "🎓",
    description: "",
    base_brain_id: "",
    system_prompt: "You are a helpful, precise, and concise assistant. Always cite the reasoning behind your answers.",
    few_shot: [],
    temperature: 0.7,
    max_tokens: 1500,
    tone: "professional",
    guardrails: { style: "", format: "", banned: [] },
    tags: [],
    is_public: false,
  });

  useEffect(() => {
    Promise.all([
      api.get("/models").then((r) => r.data),
      api.get("/custom_brains").then((r) => r.data).catch(() => ({ brains: [] })),
    ]).then(([mods, cbs]) => {
      const catalog = (mods.models || []).filter((m) => (m.capabilities || []).includes("chat") || !m.capabilities);
      const custom = (cbs.brains || []).map((b) => ({
        id: b.brain_id,
        name: b.name,
        family: b.family,
        context: b.context,
        capabilities: b.capabilities || ["chat"],
        tier: "custom",
      }));
      const list = [...catalog, ...custom];
      setBrains(list);
      if (list.length && !form.base_brain_id) setForm((f) => ({ ...f, base_brain_id: list[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addExample = () => {
    if (form.few_shot.length >= 5) return;
    setField("few_shot", [...form.few_shot, { user: "", assistant: "" }]);
  };
  const setExample = (i, key, val) => {
    const arr = [...form.few_shot];
    arr[i] = { ...arr[i], [key]: val };
    setField("few_shot", arr);
  };
  const removeExample = (i) => setField("few_shot", form.few_shot.filter((_, k) => k !== i));

  const canAdvance = () => {
    if (step === 1) return form.name.trim().length > 1;
    if (step === 2) return !!form.base_brain_id;
    if (step === 3) return form.system_prompt.trim().length > 4;
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      let id = savedId;
      if (!id) {
        const r = await api.post("/ai-models/composed", form);
        id = r.data.ai_model_id;
        setSavedId(id);
      } else {
        // Model was already created by preview — sync any final edits before navigating
        await api.patch(`/ai-models/${id}`, {
          name: form.name,
          avatar: form.avatar,
          description: form.description,
          system_prompt: form.system_prompt,
          few_shot: form.few_shot,
          temperature: form.temperature,
          max_tokens: form.max_tokens,
          tone: form.tone,
          guardrails: form.guardrails,
          tags: form.tags,
          is_public: form.is_public,
        });
      }
      setTimeout(() => nav(`/studio/${id}`), 500);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    // Save-first, then stream via /ai-models/{id}/test which resolves the composed
    // persona properly through providers.chat_stream. This gives users a genuine
    // preview of what the model will do once saved.
    if (!form.base_brain_id || !form.system_prompt.trim()) {
      setTestOutput("⚠️ Pick a base brain and write a mission before previewing.");
      return;
    }
    setTesting(true);
    setTestOutput("");
    try {
      // Create the model (or reuse if we already have savedId — this makes preview idempotent)
      let modelId = savedId;
      if (!modelId) {
        const r = await api.post("/ai-models/composed", form);
        modelId = r.data.ai_model_id;
        setSavedId(modelId);
      } else {
        // If user edited the form since last preview, update the saved model
        await api.patch(`/ai-models/${modelId}`, {
          system_prompt: form.system_prompt,
          few_shot: form.few_shot,
          temperature: form.temperature,
          max_tokens: form.max_tokens,
          tone: form.tone,
          guardrails: form.guardrails,
        });
      }

      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/ai-models/${modelId}/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: testPrompt }),
      });
      if (!res.body) throw new Error("No stream body");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "delta" && ev.content) setTestOutput((t) => t + ev.content);
            if (ev.type === "error") setTestOutput((t) => t + `\n\n⚠️ ${ev.error}`);
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      setTestOutput(`⚠️ ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const stepPill = (n, label) => (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 font-heading font-bold text-xs uppercase tracking-widest ${step === n ? "bg-slate-900 text-white border-slate-900" : n < step ? "bg-amber-200 border-slate-900 text-slate-900" : "bg-white border-slate-300 text-slate-500"}`}>
      {n < step ? <Check size={12} strokeWidth={3} /> : <span className="w-4 text-center">{n}</span>}
      {label}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="back-to-studio">
          <ArrowLeft size={14} strokeWidth={2.5} /> Studio
        </Link>
      </div>
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <Wand2 size={12} strokeWidth={2.5} /> Compose a Model
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="compose-title">Design your bespoke brain</h1>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="compose-steps">
        {stepPill(1, "Identity")}
        {stepPill(2, "Base Brain")}
        {stepPill(3, "Mission")}
        {stepPill(4, "Examples")}
        {stepPill(5, "Preview")}
      </div>

      <div className="brut-card p-6 space-y-5" data-testid={`compose-step-${step}`}>
        {step === 1 && (
          <>
            <Field label="Model name" required>
              <input className="brut-input w-full" placeholder="e.g. Precise Legal Analyst" value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="input-name" />
            </Field>
            <div className="grid grid-cols-[100px_1fr] gap-4">
              <Field label="Avatar">
                <input className="brut-input w-full text-3xl text-center" maxLength={4} value={form.avatar} onChange={(e) => setField("avatar", e.target.value)} data-testid="input-avatar" />
              </Field>
              <Field label="Tags (comma-separated)">
                <input className="brut-input w-full" placeholder="legal, analysis, contracts" value={(form.tags || []).join(", ")} onChange={(e) => setField("tags", e.target.value.split(",").map((t) => t.trim()))} data-testid="input-tags" />
              </Field>
            </div>
            <Field label="Description">
              <textarea className="brut-input w-full h-20" placeholder="What this model is best at, and how it should be used." value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="input-description" />
            </Field>
          </>
        )}

        {step === 2 && (
          <>
            <Field label="Pick the base brain" required>
              <p className="text-sm text-slate-500 mb-3">Your composed model is a persona layer on top of one of the 12 catalog brains — pick whichever nails your target behavior.</p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="brain-grid">
                {brains.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setField("base_brain_id", b.id)}
                    className={`text-left brut-card p-3 hover:-translate-y-0.5 transition-transform ${form.base_brain_id === b.id ? "ring-2 ring-amber-400 bg-amber-50" : ""}`}
                    data-testid={`brain-option-${b.id}`}
                  >
                    <div className="font-heading font-bold text-sm">{b.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{b.family}</div>
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{b.tagline || b.description || "—"}</div>
                  </button>
                ))}
              </div>
            </Field>
          </>
        )}

        {step === 3 && (
          <>
            <Field label="Mission / system prompt" required>
              <textarea className="brut-input w-full h-40" placeholder="You are a…" value={form.system_prompt} onChange={(e) => setField("system_prompt", e.target.value)} data-testid="input-system" />
              <p className="text-xs text-slate-500 mt-1">This is the north star for every response. Be specific about role, expertise, and behavior.</p>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tone">
                <select className="brut-input w-full" value={form.tone} onChange={(e) => setField("tone", e.target.value)} data-testid="input-tone">
                  {["professional", "casual", "concise", "creative", "friendly", "authoritative", "sarcastic"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label={`Temperature (${form.temperature})`}>
                <input type="range" min="0" max="1.5" step="0.05" value={form.temperature} onChange={(e) => setField("temperature", parseFloat(e.target.value))} className="w-full" data-testid="input-temperature" />
              </Field>
            </div>
            <details className="border-2 border-slate-200 rounded-xl p-3">
              <summary className="cursor-pointer text-sm font-heading font-bold">Guardrails (optional)</summary>
              <div className="mt-3 space-y-3">
                <Field label="Style guide (short instruction)">
                  <input className="brut-input w-full" placeholder="e.g. Use bullet points. Avoid jargon." value={form.guardrails?.style || ""} onChange={(e) => setField("guardrails", { ...(form.guardrails || {}), style: e.target.value })} data-testid="input-style" />
                </Field>
                <Field label="Output format">
                  <input className="brut-input w-full" placeholder="e.g. JSON, Markdown table, plain text" value={form.guardrails?.format || ""} onChange={(e) => setField("guardrails", { ...(form.guardrails || {}), format: e.target.value })} data-testid="input-format" />
                </Field>
                <Field label="Banned words (comma-separated)">
                  <input className="brut-input w-full" placeholder="e.g. gonna, kinda" value={(form.guardrails?.banned || []).join(", ")} onChange={(e) => setField("guardrails", { ...(form.guardrails || {}), banned: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="input-banned" />
                </Field>
              </div>
            </details>
          </>
        )}

        {step === 4 && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-bold text-sm">Few-shot examples</div>
                <div className="text-xs text-slate-500">Give up to 5 example exchanges. The model learns from these before answering.</div>
              </div>
              <button onClick={addExample} disabled={form.few_shot.length >= 5} className="brut-btn text-xs" data-testid="add-example">
                <Plus size={12} strokeWidth={3} /> Add example
              </button>
            </div>
            {form.few_shot.length === 0 && (
              <div className="text-sm text-slate-400 italic border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">No examples yet — this step is optional but boosts consistency dramatically.</div>
            )}
            {form.few_shot.map((p, i) => (
              <div key={i} className="brut-card p-3 space-y-2" data-testid={`example-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-heading font-bold uppercase tracking-widest text-slate-500">Example {i + 1}</div>
                  <button onClick={() => removeExample(i)} className="text-red-500 hover:text-red-700" data-testid={`remove-example-${i}`}><X size={14} /></button>
                </div>
                <textarea className="brut-input w-full h-16 text-sm" placeholder="User asks…" value={p.user} onChange={(e) => setExample(i, "user", e.target.value)} data-testid={`example-user-${i}`} />
                <textarea className="brut-input w-full h-20 text-sm" placeholder="Model responds…" value={p.assistant} onChange={(e) => setExample(i, "assistant", e.target.value)} data-testid={`example-assistant-${i}`} />
              </div>
            ))}
          </>
        )}

        {step === 5 && (
          <>
            <Field label="Try a sample prompt">
              <textarea className="brut-input w-full h-16" value={testPrompt} onChange={(e) => setTestPrompt(e.target.value)} data-testid="test-prompt" />
              <div className="flex gap-2 mt-2">
                <button onClick={runTest} disabled={testing || !form.base_brain_id} className="brut-btn text-sm" data-testid="run-test">
                  <Play size={12} strokeWidth={3} /> {testing ? "Streaming…" : "Run preview"}
                </button>
              </div>
            </Field>
            {testOutput && (
              <div className="brut-card bg-slate-50 p-4 text-sm whitespace-pre-wrap font-mono" data-testid="test-output">{testOutput}</div>
            )}
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500">Summary</div>
              <SummaryRow k="Name" v={form.name} />
              <SummaryRow k="Base brain" v={form.base_brain_id} />
              <SummaryRow k="Tone" v={form.tone} />
              <SummaryRow k="Temperature" v={form.temperature} />
              <SummaryRow k="Few-shot examples" v={form.few_shot.length} />
              <label className="flex items-center gap-2 text-sm" data-testid="share-toggle-wrap">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setField("is_public", e.target.checked)} data-testid="share-toggle" />
                <span>Share with the community (appears on the Model Studio public list)</span>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="brut-btn text-sm bg-white text-slate-900 disabled:opacity-40"
          data-testid="prev-step"
        >
          <ArrowLeft size={12} strokeWidth={3} /> Back
        </button>
        {step < 5 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canAdvance()}
            className="brut-btn text-sm disabled:opacity-40"
            data-testid="next-step"
          >
            Next <ArrowRight size={12} strokeWidth={3} />
          </button>
        ) : (
          <button onClick={save} disabled={saving || !form.name.trim()} className="brut-btn text-sm disabled:opacity-40" data-testid="save-model">
            <Sparkles size={12} strokeWidth={3} /> {saving ? "Saving…" : savedId ? "Saved ✓" : "Save model"}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
    </div>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500 uppercase tracking-widest text-[10px] font-heading font-bold">{k}</span>
      <span className="font-mono text-slate-900">{String(v || "—")}</span>
    </div>
  );
}
